import React, { useEffect, useMemo, useState } from "react";

// ---------- small UI primitives (match your palette) ----------

function Pill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={[
        "min-h-[44px] px-3 py-2 rounded-lg text-sm font-medium transition-all border",
        "touch-manipulation",
        active
          ? "bg-white text-gray-900 border-gray-300 shadow-sm"
          : "bg-white/60 text-gray-600 border-white/40 hover:bg-white/80",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-mono bg-white/70 border border-white/60 text-gray-800">
      {children}
    </span>
  );
}

function CodeCard({ children }) {
  return (
    <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-xl shadow-sm p-4">
      <pre className="font-mono text-base sm:text-lg text-gray-900 whitespace-pre-wrap leading-relaxed">
        {children}
      </pre>
    </div>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div className="bg-white/80 border border-white/50 rounded-xl p-5">
      {(title || subtitle) && (
        <div className="mb-3">
          {subtitle && <div className="text-xs text-gray-500 mb-1">{subtitle}</div>}
          {title && <div className="font-bold text-gray-900">{title}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

function Accordion({ title, open, onToggle, children }) {
  return (
    <div className="bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 rounded-xl p-5">
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-4 text-left">
        <div>
          <div className="font-bold text-gray-900">{title}</div>
          <div className="text-sm text-gray-600">Optional — open when you’re stuck.</div>
        </div>
        <div
          className={[
            "shrink-0 w-9 h-9 rounded-lg border border-white/60 bg-white/60 flex items-center justify-center transition-transform",
            open ? "rotate-180" : "rotate-0",
          ].join(" ")}
          aria-hidden="true"
        >
          <span className="text-gray-700">⌄</span>
        </div>
      </button>

      {open && <div className="mt-4">{children}</div>}
    </div>
  );
}

// ---------- modal ----------

export default function ExerciseHelpModal({
  open,
  onClose,
  exercise, // current exercise object: {id, patterns, examples, studentCore, extraSpecific, teacher...}
  course, // { metadata, GlossaryPanel, shared } from loadCourse
  initialTab = "student",
}) {
  const [tab, setTab] = useState(initialTab); // student | teacher | glossary
  const [extraOpen, setExtraOpen] = useState(false);

  const hasExtra = (exercise?.extraSpecific?.length ?? 0) > 0;

  // Safe teacher extraction
  const focus = exercise?.teacher?.focus ?? [];
  const actions = exercise?.teacher?.actions ?? [];
  const articulation = exercise?.teacher?.articulation ?? [];
  const extraExercises = exercise?.teacher?.extraExercises ?? [];

  // Reset tab/accordion when exercise changes
  useEffect(() => {
    if (!open) return;
    setTab(initialTab);
    setExtraOpen(false);
  }, [open, exercise?.id, initialTab]);

  // Lock body scroll + ESC
  useEffect(() => {
    if (!open) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const title = useMemo(() => {
    const base = course?.metadata?.title || "Exercise help";
    const exId = exercise?.id ? ` • ${exercise.id}` : "";
    return `${base}${exId}`;
  }, [course?.metadata?.title, exercise?.id]);

  if (!open) return null;

  // If no exercise passed, show a graceful fallback
  if (!exercise) {
    return (
      <div className="fixed inset-0 z-[90]">
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onPointerDown={(e) => {
            e.preventDefault();
            onClose?.();
          }}
        />
        <div
          className="absolute inset-0 flex items-center justify-center p-4"
          onPointerDown={() => onClose?.()}
        >
          <div
            onPointerDown={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-white/90 backdrop-blur-sm border border-white/50 rounded-2xl shadow-xl overflow-hidden"
          >
            <div className="p-5 border-b border-white/40 flex items-center justify-between">
              <div className="font-bold text-gray-900">Exercise help</div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-xl bg-white/70 border border-white/60 hover:bg-white flex items-center justify-center"
                aria-label="Close"
                type="button"
              >
                ×
              </button>
            </div>
            <div className="p-6 text-center">
              <div className="text-gray-700 font-medium">No help available for this file yet.</div>
              <div className="text-sm text-gray-500 mt-2">
                (Your <span className="font-mono">course_key</span> exists, but the exercise id could not be matched.)
              </div>
              <button
                onClick={onClose}
                className="mt-5 px-4 py-2 rounded-lg bg-white/80 border border-white/60 hover:bg-white text-sm font-medium text-gray-800"
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const Glossary = course?.GlossaryPanel || null;

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onPointerDown={(e) => {
          e.preventDefault();
          onClose?.();
        }}
      />

      {/* Fullscreen click-catcher */}
      <div
        className="absolute inset-0 flex items-center justify-center p-3 sm:p-4"
        onPointerDown={() => onClose?.()}
      >
        {/* Panel (stop propagation so inside clicks do not close) */}
        <div
          onPointerDown={(e) => e.stopPropagation()}
          className={[
            "w-full max-w-5xl",
            "bg-gradient-to-br from-blue-50 via-white to-purple-50",
            "border border-white/60 shadow-2xl overflow-hidden",
            "rounded-none sm:rounded-2xl",
            "flex flex-col",
            "max-h-[100svh] sm:max-h-[90svh]",
          ].join(" ")}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white/70 backdrop-blur-sm border-b border-white/60">
            <div className="px-5 py-4 flex items-start justify-between gap-4">
              <div>
                <div className="text-xs text-gray-500">
                  {course?.metadata?.author ? `by ${course.metadata.author}` : "Guidelines"}
                  {course?.metadata?.edition ? ` • ${course.metadata.edition}` : ""}
                </div>
                <div className="text-lg sm:text-xl font-bold text-gray-900">{title}</div>
              </div>

              <button
                onClick={onClose}
                className="shrink-0 w-10 h-10 rounded-xl bg-white/70 border border-white/60 hover:bg-white flex items-center justify-center text-gray-700"
                aria-label="Close"
                type="button"
              >
                ×
              </button>
            </div>

            {/* Tabs */}
            <div className="px-5 pb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-2 overflow-x-auto pb-1 pr-2 [-webkit-overflow-scrolling:touch]">
                <Pill active={tab === "student"} onClick={() => setTab("student")}>
                  Student
                </Pill>
                <Pill active={tab === "teacher"} onClick={() => setTab("teacher")}>
                  Teacher
                </Pill>
                <Pill active={tab === "glossary"} onClick={() => setTab("glossary")}>
                  Glossary
                </Pill>
              </div>

              <div className="text-xs text-gray-500">ESC closes • Click outside to close</div>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 sm:p-6 overflow-y-auto flex-1 min-h-0 h-full overscroll-contain [-webkit-overflow-scrolling:touch]">
            {/* Common: patterns + examples */}
            <div className="text-center space-y-3 mb-6">
              <div className="text-gray-600 text-sm">Rhythm patterns</div>
              <div className="flex flex-wrap justify-center gap-2">
                {(exercise.patterns || []).map((p) => (
                  <Chip key={p}>{p}</Chip>
                ))}
              </div>
              <div className="text-gray-500 text-sm">Examples: {(exercise.examples ?? []).join(" · ")}</div>
            </div>

            {/* STUDENT TAB */}
            {tab === "student" && (
              <div className="space-y-4">
                <SectionCard title="Main idea" subtitle="Student">
                  <div className="text-center text-gray-700">Say it as one wave — no pauses.</div>
                </SectionCard>

                {(exercise.studentCore?.length ?? 0) > 0 ? (
                  <div className="space-y-3">
                    {(exercise.studentCore ?? []).map((line, idx) => (
                      <CodeCard key={idx}>{line}</CodeCard>
                    ))}
                  </div>
                ) : (
                  <SectionCard>
                    <div className="text-center text-sm text-gray-600">No student core lines for this exercise yet.</div>
                  </SectionCard>
                )}

                {/* Extra as expandable on same page */}
                {hasExtra && (
                  <Accordion title="Extra tips" open={extraOpen} onToggle={() => setExtraOpen((v) => !v)}>
                    <ul className="list-disc pl-5 text-gray-700 space-y-2">
                      {(exercise.extraSpecific ?? []).map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                   
                  </Accordion>
                )}
              </div>
            )}

            {/* TEACHER TAB */}
            {tab === "teacher" && (
              <div className="space-y-4">
                {focus.length > 0 && (
                  <SectionCard subtitle="Teacher focus">
                    <ul className="list-disc pl-5 text-gray-900 space-y-2">
                      {focus.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </SectionCard>
                )}

                {articulation.length > 0 && (
                  <SectionCard subtitle="Articulation notes">
                    <ul className="list-disc pl-5 text-gray-900 space-y-2">
                      {articulation.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </SectionCard>
                )}

                {actions.length > 0 && (
                  <SectionCard subtitle="In-class actions">
                    <ul className="list-disc pl-5 text-gray-900 space-y-2">
                      {actions.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                    <div className="mt-3 text-xs text-gray-500">
                      Tip: correct pauses first (flow), then stress placement, then small sound details.
                    </div>
                  </SectionCard>
                )}

                {extraExercises.length > 0 && (
                  <div className="bg-gradient-to-r from-blue-100 to-purple-100 border border-blue-200 rounded-xl p-5">
                    <div className="font-bold text-gray-900 mb-2">Extra exercises</div>
                    <ul className="list-disc pl-5 text-gray-700 space-y-2">
                      {extraExercises.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {focus.length === 0 &&
                  actions.length === 0 &&
                  articulation.length === 0 &&
                  extraExercises.length === 0 && (
                    <SectionCard>
                      <div className="text-center text-sm text-gray-600">No teacher notes for this exercise yet.</div>
                    </SectionCard>
                  )}
              </div>
            )}

            {/* GLOSSARY TAB */}
            {tab === "glossary" && (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-xl p-5">
                  <div className="font-bold text-gray-900 mb-2">Glossary</div>
                  <div className="text-sm text-gray-600">
                    Quick definitions you can reference while doing this exercise.
                  </div>
                </div>

                {Glossary ? (
                  <Glossary compact />
                ) : (
                  <SectionCard>
                    <div className="text-center text-sm text-gray-600">No glossary for this course yet.</div>
                  </SectionCard>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-white/60 bg-white/60 backdrop-blur-sm px-5 py-4 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white/80 border border-white/60 hover:bg-white text-sm font-medium text-gray-800"
              type="button"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
